import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Loader2, UserPlus, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';

export default function RegulatorInviteManager() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [email, setEmail] = useState('');
  const [authority, setAuthority] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  async function loadCompanies() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, jurisdiction')
        .order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function sendInvite() {
    if (!email || !authority) {
      toast.error('Email and authority name are required');
      return;
    }

    if (selectedCompanies.length === 0) {
      toast.error('Please select at least one company');
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('issue-regulator-invite', {
        body: {
          email,
          authority,
          companies: selectedCompanies,
        },
      });

      if (error) throw error;

      toast.success('Regulator invite sent successfully');
      setIsOpen(false);
      resetForm();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  }

  function resetForm() {
    setEmail('');
    setAuthority('');
    setSelectedCompanies([]);
  }

  function toggleCompany(companyId: string) {
    setSelectedCompanies((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  }

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Mail className="h-5 w-5 text-primary" />
          Issue Regulator Invites
        </CardTitle>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <UserPlus className="h-4 w-4 mr-1" />
              Invite Regulator
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invite Regulator</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="email">Regulator Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="regulator@authority.gov"
                />
              </div>
              <div>
                <Label htmlFor="authority">Authority Name</Label>
                <Input
                  id="authority"
                  value={authority}
                  onChange={(e) => setAuthority(e.target.value)}
                  placeholder="e.g., CIPC, SEC, FSB"
                />
              </div>
              <div>
                <Label>Assign Companies</Label>
                {loading ? (
                  <div className="flex justify-center p-4">
                    <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
                  </div>
                ) : (
                  <div className="border rounded-md p-4 max-h-64 overflow-y-auto space-y-2">
                    {companies.length > 0 ? (
                      companies.map((company) => (
                        <div key={company.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={company.id}
                            checked={selectedCompanies.includes(company.id)}
                            onCheckedChange={() => toggleCompany(company.id)}
                          />
                          <label
                            htmlFor={company.id}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {company.name}
                            {company.jurisdiction && (
                              <span className="text-muted-foreground ml-2">
                                ({company.jurisdiction})
                              </span>
                            )}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No companies available
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Selected: {selectedCompanies.length} companies
                </p>
              </div>
              <Button onClick={sendInvite} disabled={sending || loading} className="w-full">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Invite'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Issue role-based regulator access with assigned companies. Regulators will have read-only
          access to audit data for their assigned companies.
        </p>
      </CardContent>
    </Card>
  );
}
